// Internal cost-breakdown grid (spec §3.1).
// Rows = cost line items grouped by section.
// Columns = 6 quantity breaks. Unused columns render blank.

const fmt = (n, places = 2) =>
  n == null || Number.isNaN(n)
    ? ''
    : `$${Number(n).toLocaleString(undefined, { minimumFractionDigits: places, maximumFractionDigits: places })}`;

const fmtQty = (n) => (n == null || n === 0 ? '' : String(n));

const SECTIONS = [
  {
    title: 'Setup & Programming',
    rows: [
      { label: 'Setup / piece',       key: 'setup_per_piece' },
      { label: 'Programming / piece', key: 'programming_per_piece' },
      { label: 'Total Setup / piece', key: 'total_setup_prog_per_piece', bold: true },
    ],
  },
  {
    title: 'Fabrication',
    rows: [
      { label: 'Deburring / piece',   key: 'deburring_per_piece' },
      { label: 'Runtime / piece',     key: 'runtime_per_piece' },
      { label: 'Secondary / piece',   key: 'secondary_per_piece' },
      { label: 'Total Fab / piece',   key: 'total_fab_per_piece', bold: true },
    ],
  },
  {
    title: 'Cost of Goods',
    rows: [
      { label: 'Material / piece',    key: 'material_per_piece' },
      { label: 'Plating / piece',     key: 'plating_per_piece' },
      { label: 'Tooling / piece',     key: 'tooling_per_piece' },
      { label: 'Fixturing / piece',   key: 'fixturing_per_piece' },
      { label: 'Inspection / piece',  key: 'inspection_per_piece' },
      { label: 'Packaging / piece',   key: 'packaging_per_piece' },
      { label: 'Shipping / piece',    key: 'shipping_per_piece' },
      { label: 'Total COG / piece',   key: 'total_cog_per_piece', bold: true },
    ],
  },
  {
    title: 'Quote Price',
    rows: [
      { label: 'Subtotal / piece',          key: 'subtotal_per_piece' },
      { label: 'Markup / piece',            key: 'markup_per_piece' },
      { label: 'Price (pre-floor/round)',   key: 'price_before_floor_rounding' },
      { label: 'Minimum Lot / piece',       key: 'min_lot_price_per_piece' },
      { label: 'Calculated / piece',        key: 'calculated_price_per_piece' },
      { label: 'Rounded Price / piece',     key: 'rounded_price_per_piece', highlight: true },
      { label: 'Extended Price',            key: 'extended_price', highlight: true },
    ],
  },
];

export default function QuoteInternalEstimate({ results }) {
  // results: array of 6 (QuantityBreakResult or null)
  const padded = [...(results || []), null, null, null, null, null, null].slice(0, 6);
  const quantities = padded.map((r) => r?.quantity ?? null);

  return (
    <div className="bg-white border border-slate-200 rounded-sm overflow-hidden">
      <div className="px-3 py-2 border-b border-slate-200 bg-slate-50">
        <h3 className="text-xs font-semibold text-slate-700 uppercase tracking-wide">
          Internal Estimate
        </h3>
        <p className="text-xs text-slate-500 mt-0.5">Never shown to customer.</p>
      </div>
      <div className="overflow-x-auto">
        <table className="min-w-full text-xs">
          <thead>
            <tr className="bg-slate-50 border-b border-slate-200">
              <th className="px-3 py-1.5 text-left font-medium text-slate-500 sticky left-0 bg-slate-50 w-48">
                Quantity
              </th>
              {quantities.map((q, idx) => (
                <th key={idx} className="px-3 py-1.5 text-right font-medium text-slate-700 tabular-nums w-24">
                  {fmtQty(q) || <span className="text-slate-300 font-normal">—</span>}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {SECTIONS.map((section) => (
              <>
                <tr key={`${section.title}-h`} className="bg-slate-100/60">
                  <td colSpan={7} className="px-3 py-1 text-xs font-semibold text-slate-600 uppercase tracking-wide">
                    {section.title}
                  </td>
                </tr>
                {section.rows.map((row) => (
                  <tr
                    key={row.key}
                    className={`border-b border-slate-50 ${row.highlight ? 'bg-emerald-50/40' : ''}`}
                  >
                    <td className={`px-3 py-1.5 text-slate-600 sticky left-0 bg-white ${row.bold ? 'font-semibold text-slate-800' : ''} ${row.highlight ? 'bg-emerald-50/40 font-semibold text-emerald-900' : ''}`}>
                      {row.label}
                    </td>
                    {padded.map((r, idx) => (
                      <td
                        key={idx}
                        className={`px-3 py-1.5 text-right tabular-nums ${row.bold ? 'font-semibold text-slate-800' : 'text-slate-700'} ${row.highlight ? 'text-emerald-900 font-semibold' : ''}`}
                      >
                        {r ? fmt(r[row.key]) : <span className="text-slate-300">—</span>}
                      </td>
                    ))}
                  </tr>
                ))}
              </>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}
